import React, { useState, useEffect, useRef, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from './lib/supabase';
import './ServiceDetails.css';
import { formatPrice } from './utils/priceFormatter';
import LanguageContext from './LanguageContext';
import { translateService } from './translations';

function ServiceDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language } = useContext(LanguageContext);
  const [isPlaying, setIsPlaying] = useState(false);
  const [service, setService] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const audioRef = useRef(null);

  useEffect(() => {
    fetchServiceDetails();
  }, [id]);

  // Get translated service based on current language
  const translatedService = service ? translateService(service, language) : null;

  const fetchServiceDetails = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const { data, error: fetchError } = await supabase
        .from('services')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError) {
        throw fetchError;
      }

      if (data) {
        setService(data);
      } else {
        setError('Service not found');
      }
    } catch (err) {
      console.error('Error fetching service details:', err);
      setError('Failed to load service details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
    }
    setIsPlaying(!isPlaying);
  };

  if (isLoading) {
    return (
      <div className="service-details-page">
        <div className="container" style={{ padding: '4rem 1rem', textAlign: 'center' }}>
          <div className="loading-spinner" style={{ 
            width: '50px', 
            height: '50px', 
            border: '4px solid rgba(124, 58, 237, 0.3)',
            borderTopColor: '#7C3AED',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }}></div>
          <p>Loading service details...</p>
        </div>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="service-details-page">
        <div className="container" style={{ padding: '4rem 1rem', textAlign: 'center' }}>
          <p style={{ color: '#ef4444', fontSize: '1.2rem' }}>
            {error || 'Service not found'}
          </p>
          <button 
            onClick={() => navigate('/services')}
            style={{
              marginTop: '1rem',
              padding: '0.75rem 1.5rem',
              background: '#7C3AED',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            Back to Services
          </button>
        </div>
      </div>
    );
  }

  // Use translated service for display
  const displayService = translatedService || service;

  return (
    <div className="service-details-page">
      {/* Service Header */}
      <section className="service-details-header">
        <div className="container">
          <div className="header-content">
            <h1 className="service-details-title">{displayService.name}</h1>
            <div className="service-type-badge-large">
              {displayService.type} Session
            </div>
          </div>
        </div>
        <div className="wave-divider-header"></div>
      </section>

      {/* Service Visual */}
      <section className="service-visual-section">
        <div className="container">
          <div className="service-image-container">
            <img 
              src={service.image || 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=1200&h=600&fit=crop&auto=format'} 
              alt={displayService.name}
              className="service-details-image"
            />
            <div className="image-glow"></div>
          </div>
        </div>
      </section>

      {/* Service Summary */}
      <section className="service-summary-section">
        <div className="container">
          <div className="summary-grid">
            <div className="summary-item">
              <div className="summary-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 13H11V7H13V13ZM13 17H11V15H13V17Z" fill="currentColor"/>
                </svg>
              </div>
              <div className="summary-content">
                <span className="summary-label">Duration</span>
                <span className="summary-value">{displayService.duration}</span>
              </div>
            </div>
            <div className="summary-item">
              <div className="summary-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 17C11.45 17 11 16.55 11 16C11 15.45 11.45 15 12 15C12.55 15 13 15.45 13 16C13 16.55 12.55 17 12 17ZM13 13H11V7H13V13Z" fill="currentColor"/>
                </svg>
              </div>
              <div className="summary-content">
                <span className="summary-label">Price</span>
                <span className="summary-value">{formatPrice(displayService.price)}</span>
              </div>
            </div>
            <div className="summary-item">
              <div className="summary-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="currentColor"/>
                </svg>
              </div>
              <div className="summary-content">
                <span className="summary-label">Session Type</span>
                <span className="summary-value">{displayService.type}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Full Description */}
      <section className="description-section">
        <div className="container">
          <div className="description-content">
            <h2 className="section-title">About This Session</h2>
            <p className="service-description-full">{displayService.description}</p>
          </div>
        </div>
      </section>

      {/* PDF Document Section */}
      {service.pdf_url && (
        <section className="pdf-section">
          <div className="container">
            <h2 className="section-title">Service Documents</h2>
            <div className="pdf-download-container">
              <div className="pdf-icon-wrapper">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                </svg>
              </div>
              <div className="pdf-content">
                <h3 className="pdf-title">Service Information PDF</h3>
                <p className="pdf-description">Download detailed information about this service</p>
                <a 
                  href={service.pdf_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-download-pdf"
                  download
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="7 10 12 15 17 10"></polyline>
                    <line x1="12" y1="15" x2="12" y2="3"></line>
                  </svg>
                  Download PDF
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Audio Support Section */}
      {service.audio_url && (
        <section className="audio-section">
          <div className="container">
            <h2 className="section-title">Meditation Audio</h2>
            <div className="audio-player-container">
              <div className="audio-visual">
                <div className="waveform">
                  {[...Array(20)].map((_, i) => (
                    <div 
                      key={i} 
                      className={`wave-bar ${isPlaying ? 'playing' : ''}`}
                      style={{ animationDelay: `${i * 0.1}s` }}
                    ></div>
                  ))}
                </div>
              </div>
              <div className="audio-controls">
                <button 
                  className="audio-play-button"
                  onClick={handlePlayPause}
                  aria-label={isPlaying ? 'Pause' : 'Play'}
                >
                  {isPlaying ? (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 4H10V20H6V4ZM14 4H18V20H14V4Z" fill="currentColor"/>
                    </svg>
                  ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 5V19L19 12L8 5Z" fill="currentColor"/>
                    </svg>
                  )}
                </button>
                <div className="audio-info">
                  <span className="audio-label">Meditation Audio</span>
                  <span className="audio-note">Meditation audio for this session</span>
                </div>
              </div>
              <audio 
                ref={audioRef}
                src={service.audio_url}
                loop
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
              />
            </div>
          </div>
        </section>
      )}

      {/* Booking Call to Action */}
      <section className="booking-section">
        <div className="container">
          <div className="booking-content">
            <button 
              className="btn-book-session"
              onClick={() => navigate(`/book/${id}`)}
            >
              Book This Session
            </button>
            <p className="booking-note">
              You'll receive a confirmation email with session details and preparation instructions. 
              Payment is required to secure your booking.
            </p>
          </div>
        </div>
      </section>

      {/* Gentle Closure */}
      <footer className="service-details-footer">
        <div className="wave-footer-closure"></div>
      </footer>
    </div>
  );
}

export default ServiceDetails;

